import React from 'react'
import moment from 'moment'
import { TFunction } from 'next-i18next'
import { urlRE } from 'src/core/helpers'
import { AppDispatch } from 'src/app/store'
import { setProjectFormItemValue } from 'src/features'
import type { BaseType } from 'antd/lib/typography/Base'
import { ProjectFormDocumentationFiles, ProjectKindType } from 'src/core/models'
import ProjectFormRegistryName from './ProjectFormRegistryName/ProjectFormRegistryName'
import {
  CustomFileInput,
  CustomTextInput,
  CustomUrlInput,
  CustomDatePicker
} from 'src/components'

interface InputProps {
    name: string
    required?: boolean
    onChangeFileUrl?: Function
    onChange?: Function
    placeholder?: string
    value?: string
    noSpacedAllowed?: boolean
    isRegistryUrl?: boolean
}

interface DocumentationSection {
    rowClassName?: string
    isVisible: boolean
    isOnlyComponent?: boolean
    labels?: {
        title: string
        subtitle: string
        thirdLabel?: string
        thirdLabelType?: BaseType
        className?: string
    }
    form: {
        // eslint-disable-next-line no-undef
        component: React.FunctionComponent<any>
        props?: InputProps
    }
}

export const documentationSections = (
  t: TFunction,
  dispatch: AppDispatch,
  fileValues: ProjectFormDocumentationFiles,
  isActive: boolean,
  kindSelected: ProjectKindType
): DocumentationSection[] => [
  {
    // Registry id
    isVisible: true,
    labels: {
      title: t('Projects.Form.Registry'),
      subtitle: t('Projects.Form.RegistryIdLabel')
    },
    form: {
      component: CustomTextInput,
      props: {
        noSpacedAllowed: true,
        name: 'registry_depositing_id',
        placeholder: t('Projects.Form.IdPlaceholder'),
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(
            setProjectFormItemValue({
              key: 'registry_depositing_id',
              value: e.target.value
            })
          )
        },
        required: kindSelected === 'carbon_forward' ? false : isActive
      }
    }
  },

  {
    // Name of Registry,
    isVisible: true,
    isOnlyComponent: true,
    form: {
      component: ProjectFormRegistryName
    }
  },

  {
    // Url of Registry,
    isVisible: true,
    labels: {
      title: t('Projects.Form.UrlLabelTwo'),
      subtitle: t('Projects.Form.UrlValidation')
    },
    form: {
      component: CustomUrlInput,
      props: {
        name: 'registry_depositing_url',
        placeholder: t('Projects.Form.LinkPlaceholder'),
        isRegistryUrl: true,
        onChange: (value: string) => {
          const url = urlRE.test(value)
          if (url) {
            dispatch(
              setProjectFormItemValue({
                key: 'registry_depositing_url',
                value: value
              })
            )
          }
        },
        required: kindSelected === 'carbon_forward' ? false : isActive
      }
    }
  },

  {
    // Credits Owner Certificate,
    isVisible: ['compensation', 'biodiversity', 'carbon_forward'].includes(
      kindSelected
    ),
    rowClassName: 'mt-4',
    labels: {
      title: t('Projects.Form.CreditFile'),
      subtitle: t('Projects.Form.CreditFileLabel'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'credits_certificate',
        value: fileValues.credits_certificate,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'credits_certificate',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // Protocol Implementation Scheme
    isVisible: kindSelected === 'biodiversity',
    rowClassName: 'mt-4',
    labels: {
      title: t('Protocol Implementation Scheme'),
      subtitle: t('Projects.Form.ResultingDoc'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'protocol_implementation_scheme',
        value: fileValues.protocol_implementation_scheme,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'protocol_implementation_scheme',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // Executive Summary
    isVisible: kindSelected === 'biodiversity',
    rowClassName: 'mt-4',
    labels: {
      title: t('Executive Summary'),
      subtitle: t('Projects.Form.ResultingDoc'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'executive_summary',
        value: fileValues.executive_summary,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'executive_summary',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // Additional Documentation
    isVisible: kindSelected === 'biodiversity',
    rowClassName: 'mt-4',
    labels: {
      title: t('Additional Documentation'),
      subtitle: t('Projects.Form.ResultingDoc'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'additional_documentation',
        value: fileValues.additional_documentation,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'additional_documentation',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // PDD
    isVisible: ['compensation', 'carbon_forward'].includes(kindSelected),
    labels: {
      title: t('PDD'),
      subtitle: t('Projects.Form.PDDlabel'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'pdd',
        value: fileValues.pdd,
        onChangeFileUrl: (value: string) => {
          dispatch(setProjectFormItemValue({ key: 'pdd', value }))
        },
        required: false
      }
    }
  },

  {
    // Validation Report
    isVisible: ['compensation', 'carbon_forward'].includes(kindSelected),
    labels: {
      title: t('Projects.Fields.ValidationReport'),
      subtitle: t('Projects.Form.ValidationReportLabel'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'validation_report',
        value: fileValues.validation_report,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'validation_report',
              value
            })
          )
        },
        required: false
      }
    }
  },

  {
    // Monitoring Report
    isVisible: ['compensation', 'biodiversity'].includes(kindSelected),
    labels: {
      title: t('Projects.Form.MonitoringReport'),
      subtitle: t('Projects.Form.MonitoringReportLabel'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'monitoring_report',
        value: fileValues.monitoring_report,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'monitoring_report',
              value
            })
          )
        },
        required: kindSelected === 'biodiversity' ? isActive : false
      }
    }
  },

  {
    // Verification Report
    isVisible: kindSelected === 'compensation',
    labels: {
      title: t('Projects.Form.VerificationReport'),
      subtitle: t('Projects.Form.VerificationReportlabel'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'verification_report',
        value: fileValues.verification_report,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'verification_report',
              value
            })
          )
        },
        required: false
      }
    }
  },

  {
    // Device Registration Request Document
    isVisible: kindSelected === 'energy',
    labels: {
      title: t('Projects.Fields.DeviceDoc'),
      subtitle: t('Projects.Form.ResultingDoc'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger',
      className: 'pr-1'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'device_registration_request',
        value: fileValues.device_registration_request,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'device_registration_request',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // Resulting from the Issuer Document
    isVisible: kindSelected === 'energy',
    labels: {
      title: t('Projects.Fields.DateDoc'),
      subtitle: t('Projects.Form.ResultingDoc'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'resulting_from_the_issuer',
        value: fileValues.resulting_from_the_issuer,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'resulting_from_the_issuer',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // Start-up date of the installation Document
    isVisible: kindSelected === 'energy',
    labels: {
      title: t('Projects.Fields.ResultingDoc'),
      subtitle: t('Projects.Form.DateDoc')
    },
    form: {
      component: CustomDatePicker,
      props: {
        name: 'start_up_date_of_installation',
        onChange: (date: string) => {
          const value = date
            ? moment(date).format('YYYY-MM-DD')
            : null
          dispatch(
            setProjectFormItemValue({
              key: 'start_up_date_of_installation',
              value
            })
          )
        },
        required: isActive
      }
    }
  },

  {
    // Insurance documentation
    isVisible: kindSelected === 'carbon_forward',
    labels: {
      title: t('Forward.InsuranceDocumentation'),
      subtitle: t('Forward.Max'),
      thirdLabel: t('Projects.FilesAllowedTwo'),
      thirdLabelType: 'danger'
    },
    form: {
      component: CustomFileInput,
      props: {
        name: 'insurance_documentation',
        value: fileValues.insurance_documentation,
        onChangeFileUrl: (value: string) => {
          dispatch(
            setProjectFormItemValue({
              key: 'insurance_documentation',
              value
            })
          )
        },
        required: false
      }
    }
  }
]
